/**
 * Tax pane (v0.5.6 AE-088) — upload prior-year returns, review every
 * extracted number NEXT TO the document line it was parsed from, and
 * confirm (optionally correcting) before the tax engine consumes it.
 *
 * Anti-hallucination surface: an amount is only ever shown with its
 * `source_line`; nothing here is LLM-generated.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import * as api from '../lib/api';
import { useToast } from '../lib/toast';

const FIELD_LABELS: Record<string, string> = {
  wages: 'Wages',
  taxable_interest: 'Taxable interest',
  qualified_dividends: 'Qualified dividends',
  ordinary_dividends: 'Ordinary dividends',
  capital_gain_loss: 'Capital gain/(loss)',
  total_income: 'Total income',
  agi: 'Adjusted gross income',
  std_deduction: 'Standard deduction',
  taxable_income: 'Taxable income',
  federal_tax: 'Federal tax',
  total_tax: 'Total tax (federal)',
  federal_withholding: 'Federal withholding',
  estimated_payments: 'Estimated payments',
  sched_d_net_st: 'Sched D net short-term',
  sched_d_net_lt: 'Sched D net long-term',
  sched_d_total: 'Sched D total gain/(loss)',
  sched_d_allowed_loss: 'Allowed loss (3k limit)',
  carryover_st_in: 'ST carryover (into year)',
  carryover_lt_in: 'LT carryover (into year)',
  carryover_st_out: 'ST carryover (to next year)',
  carryover_lt_out: 'LT carryover (to next year)',
  k1_income: 'K-1 pass-through income',
  federal_agi: 'Federal AGI (per CA 540)',
  ca_agi: 'CA adjusted gross income',
  ca_std_deduction: 'CA standard deduction',
  ca_taxable_income: 'CA taxable income',
  ca_tax: 'CA tax',
  ca_total_tax: 'CA total tax',
  ca_withholding: 'CA withholding',
  ca_estimated_payments: 'CA estimated payments',
  gross_receipts: 'Gross receipts',
  cogs: 'Cost of goods sold',
  officer_comp: 'Officer compensation',
  salaries_wages: 'Salaries and wages',
  total_deductions: 'Total deductions',
  ordinary_income: 'Ordinary business income',
  ordinary_income_fed: 'Ordinary income (federal)',
  net_income: 'Net income (CA)',
  franchise_tax: 'CA franchise tax',
  distributions: 'Distributions',
};

function fmt(v: number): string {
  const sign = v < 0 ? '-' : '';
  return `${sign}$${Math.abs(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function pct(v: number): string {
  return `${(v * 100).toFixed(1)}%`;
}

export function TaxPane() {
  const toast = useToast();
  const [profiles, setProfiles] = useState<api.TaxProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [reviewing, setReviewing] = useState<api.TaxProfile | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    api.tax
      .profiles()
      .then((r) => setProfiles(r.profiles))
      .catch((e: unknown) => toast.show((e as Error).message, 'bad'))
      .finally(() => setLoading(false));
  }, [toast]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onFiles(list: FileList | null) {
    if (!list || list.length === 0) return;
    setUploading(true);
    try {
      const result = await api.tax.uploadReturns(Array.from(list));
      for (const err of result.errors) {
        toast.show(`${err.filename}: ${err.error}`, 'warn');
      }
      if (result.profiles.length > 0) {
        toast.show(
          `Parsed ${result.parsed.length} document(s) into ${result.profiles.length} profile(s) — review & confirm below`,
          'ok',
        );
        refresh();
        setReviewing(result.profiles[0]);
      }
    } catch (e: unknown) {
      toast.show((e as Error).message, 'bad');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  return (
    <div className="space-y-4" data-testid="tax-pane">
      <div className="card p-4">
        <div className="text-sm font-semibold mb-1">Upload tax returns</div>
        <div className="text-[11px] text-ink-mute mb-3">
          Prior-year 1040, CA 540, 1120-S, CA 100S, or K-1 (PDF). Every number is
          extracted from the document itself and shown next to its source line —
          you confirm before anything is used. Files are parsed and discarded;
          only the confirmed fields are stored.
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf"
          multiple
          className="hidden"
          data-testid="tax-upload-input"
          onChange={(e) => onFiles(e.target.files)}
        />
        <button
          className="btn-primary text-xs"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid="tax-upload-btn"
        >
          {uploading ? 'Parsing…' : 'Choose PDFs to upload'}
        </button>
      </div>

      <div className="card p-4">
        <div className="text-sm font-semibold mb-2">Stored tax profiles</div>
        {loading ? (
          <div className="text-xs text-ink-mute">Loading…</div>
        ) : profiles.length === 0 ? (
          <div className="text-xs text-ink-mute">
            No tax profiles yet. Upload returns above — the tax engine (harvest
            scanner, marginal rates) activates once a profile is confirmed.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-ink-mute">
                <th className="py-1 pr-3">Year</th>
                <th className="py-1 pr-3">Entity</th>
                <th className="py-1 pr-3">Filing status</th>
                <th className="py-1 pr-3">Documents</th>
                <th className="py-1 pr-3">Status</th>
                <th className="py-1" />
              </tr>
            </thead>
            <tbody>
              {profiles.map((p) => (
                <tr key={p.id} className="border-t border-edge" data-testid={`tax-profile-${p.tax_year}-${p.entity}`}>
                  <td className="py-1.5 pr-3 num">{p.tax_year}</td>
                  <td className="py-1.5 pr-3">
                    {p.entity === 's_corp' ? (p.entity_name ?? 'S-Corp') : 'Personal'}
                  </td>
                  <td className="py-1.5 pr-3">{p.filing_status ?? '—'}</td>
                  <td className="py-1.5 pr-3">{p.documents.map((d) => d.form_type).join(', ')}</td>
                  <td className="py-1.5 pr-3">
                    {p.status === 'confirmed' ? (
                      <span className="pill-sim">confirmed</span>
                    ) : (
                      <span className="pill text-warn border-[#5c4720]">needs review</span>
                    )}
                  </td>
                  <td className="py-1.5 text-right">
                    <button className="btn text-[11px]" onClick={() => setReviewing(p)}>
                      {p.status === 'confirmed' ? 'view' : 'review & confirm'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <TaxEngineCards hasConfirmed={profiles.some((p) => p.status === 'confirmed')} />

      {reviewing && (
        <TaxConfirmDialog
          profile={reviewing}
          onClose={() => setReviewing(null)}
          onConfirmed={() => {
            setReviewing(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

/**
 * Tax engine surface (AE-087): unrealized G/L by term, marginal rates
 * from the confirmed profile, TLH candidates with wash-sale flags.
 * Every number comes from /tax/* endpoints (lots + broker quotes +
 * confirmed profile); unpriced lots are labeled, never estimated.
 */
function TaxEngineCards({ hasConfirmed }: { hasConfirmed: boolean }) {
  const [unrealized, setUnrealized] = useState<api.TaxUnrealized | null>(null);
  const [tlh, setTlh] = useState<api.TaxTlh | null>(null);
  const [rates, setRates] = useState<api.TaxRates | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    Promise.allSettled([api.tax.unrealized(), api.tax.tlh(), api.tax.rates()]).then(
      ([u, t, r]) => {
        if (!alive) return;
        if (u.status === 'fulfilled') setUnrealized(u.value);
        if (t.status === 'fulfilled') setTlh(t.value);
        if (r.status === 'fulfilled') setRates(r.value);
        setLoading(false);
      },
    );
    return () => {
      alive = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="card p-4">
        <div className="text-xs text-ink-mute">Loading tax engine…</div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="tax-engine-cards">
      <div className="card p-4">
        <div className="text-sm font-semibold mb-2">Unrealized gains &amp; losses</div>
        {!unrealized || unrealized.totals.lot_count === 0 ? (
          <div className="text-xs text-ink-mute">
            No cost-basis lots yet — connect Schwab (Settings → Brokerage) and run a
            sync to import holdings with lot-level basis.
          </div>
        ) : (
          <>
            <div className="flex gap-6 mb-3 text-xs">
              <div>
                <span className="text-ink-mute">Short-term </span>
                <span className={`num ${unrealized.totals.short_unrealized < 0 ? 'text-bad' : 'text-ok'}`}>
                  {fmt(unrealized.totals.short_unrealized)}
                </span>
              </div>
              <div>
                <span className="text-ink-mute">Long-term </span>
                <span className={`num ${unrealized.totals.long_unrealized < 0 ? 'text-bad' : 'text-ok'}`}>
                  {fmt(unrealized.totals.long_unrealized)}
                </span>
              </div>
              <div>
                <span className="text-ink-mute">Lots </span>
                <span className="num">{unrealized.totals.lot_count}</span>
                {unrealized.totals.unpriced_lots > 0 && (
                  <span className="text-warn"> ({unrealized.totals.unpriced_lots} unpriced)</span>
                )}
              </div>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-ink-mute">
                  <th className="py-1 pr-3">Symbol</th>
                  <th className="py-1 pr-3">Qty</th>
                  <th className="py-1 pr-3">Opened</th>
                  <th className="py-1 pr-3">Term</th>
                  <th className="py-1 pr-3 text-right">Basis</th>
                  <th className="py-1 pr-3 text-right">Value</th>
                  <th className="py-1 text-right">Unrealized</th>
                </tr>
              </thead>
              <tbody>
                {unrealized.lots.map((l) => (
                  <tr key={l.lot_id} className="border-t border-edge" data-testid={`tax-lot-${l.lot_id}`}>
                    <td className="py-1.5 pr-3 font-semibold">{l.symbol}</td>
                    <td className="py-1.5 pr-3 num">{l.qty}</td>
                    <td className="py-1.5 pr-3 num">{l.open_date}</td>
                    <td className="py-1.5 pr-3">
                      {l.term === 'long' ? (
                        <span className="pill-sim">LT</span>
                      ) : (
                        <span className="pill" title={l.lt_on ? `long-term on ${l.lt_on}` : undefined}>
                          ST
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 pr-3 num text-right">{fmt(l.cost_basis)}</td>
                    <td className="py-1.5 pr-3 num text-right">
                      {l.market_value === null ? <span className="text-warn">no quote</span> : fmt(l.market_value)}
                    </td>
                    <td className={`py-1.5 num text-right ${l.unrealized === null ? '' : l.unrealized < 0 ? 'text-bad' : 'text-ok'}`}>
                      {l.unrealized === null ? '—' : fmt(l.unrealized)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      <div className="card p-4" data-testid="tax-tlh-card">
        <div className="text-sm font-semibold mb-1">Tax-loss harvesting</div>
        {rates && (
          <div className="text-[11px] text-ink-mute mb-2">
            Marginal rates: ST {pct(rates.short_term_combined)} · LT {pct(rates.long_term_combined)}{' '}
            ({rates.source === 'policyengine' ? 'PolicyEngine' : 'bracket tables'}
            {hasConfirmed ? `, from your confirmed ${rates.situation.state} return` : ', defaults — upload returns for real rates'})
          </div>
        )}
        {!tlh || tlh.candidates.length === 0 ? (
          <div className="text-xs text-ink-mute">
            No harvest candidates right now (threshold: loss ≥ {tlh ? fmt(tlh.thresholds.min_loss_usd) : '$200'} and ≥{' '}
            {tlh ? pct(tlh.thresholds.min_loss_pct) : '3%'}).
          </div>
        ) : (
          <>
            <div className="text-xs mb-2">
              <span className="text-ink-mute">Harvestable </span>
              <span className="num text-bad">{fmt(-tlh.summary.harvestable_loss)}</span>
              <span className="text-ink-mute"> → est. savings </span>
              <span className="num text-ok">{fmt(tlh.summary.est_tax_savings)}</span>
              {tlh.summary.wash_sale_flagged > 0 && (
                <span className="text-warn"> · {tlh.summary.wash_sale_flagged} wash-sale flagged</span>
              )}
            </div>
            <table className="w-full text-xs mb-2">
              <thead>
                <tr className="text-left text-ink-mute">
                  <th className="py-1 pr-3">Symbol</th>
                  <th className="py-1 pr-3">Term</th>
                  <th className="py-1 pr-3 text-right">Loss</th>
                  <th className="py-1 pr-3 text-right">Est. savings</th>
                  <th className="py-1 pr-3">Wash sale</th>
                  <th className="py-1">No rebuy before</th>
                </tr>
              </thead>
              <tbody>
                {tlh.candidates.map((c) => (
                  <tr key={c.lot_id} className="border-t border-edge" data-testid={`tlh-${c.symbol}`}>
                    <td className="py-1.5 pr-3 font-semibold">{c.symbol}</td>
                    <td className="py-1.5 pr-3">{c.term === 'long' ? 'LT' : 'ST'}</td>
                    <td className="py-1.5 pr-3 num text-right text-bad">{fmt(-c.loss)}</td>
                    <td className="py-1.5 pr-3 num text-right text-ok">{fmt(c.est_tax_savings)}</td>
                    <td className="py-1.5 pr-3">
                      {c.wash_sale_risk ? (
                        <span className="pill text-warn border-[#5c4720]" title="a buy fill exists in the last 30 days">
                          risk
                        </span>
                      ) : (
                        <span className="text-ink-mute">clear</span>
                      )}
                    </td>
                    <td className="py-1.5 num">{c.no_rebuy_before}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-[10px] text-ink-mute">
              {tlh.assumptions.map((a, i) => (
                <div key={i}>• {a}</div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function TaxConfirmDialog({
  profile,
  onClose,
  onConfirmed,
}: {
  profile: api.TaxProfile;
  onClose: () => void;
  onConfirmed: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [corrections, setCorrections] = useState<Record<string, string>>({});

  const entries = Object.entries(profile.fields).sort(([a], [b]) => a.localeCompare(b));

  async function confirm() {
    setBusy(true);
    try {
      const parsed: Record<string, number> = {};
      for (const [name, raw] of Object.entries(corrections)) {
        const v = Number(raw.replace(/[,$\s]/g, ''));
        if (!Number.isFinite(v)) {
          toast.show(`Correction for ${name} is not a number`, 'bad');
          setBusy(false);
          return;
        }
        parsed[name] = v;
      }
      await api.tax.confirm(profile.id, Object.keys(parsed).length ? parsed : undefined);
      toast.show(`${profile.tax_year} ${profile.entity} profile confirmed`, 'ok');
      onConfirmed();
    } catch (e: unknown) {
      toast.show((e as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="tax-confirm-dialog">
      <div className="card p-5 max-w-3xl w-full mx-4 max-h-[85vh] overflow-y-auto">
        <div className="font-semibold mb-1">
          {profile.tax_year} · {profile.entity === 's_corp' ? (profile.entity_name ?? 'S-Corp') : 'Personal'}
          {profile.state ? ` · ${profile.state}` : ''}
        </div>
        <div className="text-[11px] text-ink-mute mb-3">
          Each amount below was parsed from the exact document line shown. Edit a
          value only if the parser got it wrong — your correction is recorded as
          an operator override.
        </div>
        <table className="w-full text-xs mb-4">
          <thead>
            <tr className="text-left text-ink-mute">
              <th className="py-1 pr-2">Field</th>
              <th className="py-1 pr-2">Value</th>
              <th className="py-1">Source line ({'from your document'})</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([name, f]) => (
              <tr key={name} className="border-t border-edge align-top">
                <td className="py-1.5 pr-2 whitespace-nowrap">{FIELD_LABELS[name] ?? name}</td>
                <td className="py-1.5 pr-2 num whitespace-nowrap">
                  {profile.status === 'confirmed' ? (
                    fmt(f.value)
                  ) : (
                    <input
                      className="input w-28 text-right num"
                      defaultValue={String(f.value)}
                      onChange={(e) =>
                        setCorrections((c) =>
                          Number(e.target.value.replace(/[,$\s]/g, '')) === f.value
                            ? Object.fromEntries(Object.entries(c).filter(([k]) => k !== name))
                            : { ...c, [name]: e.target.value },
                        )
                      }
                      data-testid={`tax-field-${name}`}
                    />
                  )}
                </td>
                <td className="py-1.5 text-[10px] text-ink-mute font-mono break-all">
                  {f.source_line}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex justify-end gap-2">
          <button className="btn text-xs" onClick={onClose} disabled={busy}>
            close
          </button>
          {profile.status !== 'confirmed' && (
            <button
              className="btn-primary text-xs"
              onClick={confirm}
              disabled={busy}
              data-testid="tax-confirm-btn"
            >
              {busy
                ? 'Confirming…'
                : Object.keys(corrections).length
                  ? `Confirm with ${Object.keys(corrections).length} correction(s)`
                  : 'Confirm — numbers match my return'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
